import { useState, useEffect } from 'react';
import {
  initialBuildingFormData,
  BuildingStatuses,
  BuildingTypes,
} from '../config/formDataConfig';
import API_BASE_URL from '../../../../../Constants';

export const useBuildingFormLogic = () => {
  const [buildingFormData, setBuildingFormData] = useState(initialBuildingFormData);
  const [customers, setCustomers] = useState([]);
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customerContacts, setCustomerContacts] = useState([]);



  const fetchCustomerContacts = async (customerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/customer-contact-intersections/customer/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      setCustomerContacts(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };



  const clearCustomerContacts = () => {
    setCustomerContacts([]);
  };



  // Fetch data when the component mounts
  useEffect(() => {
    fetchCustomers();
    fetchManagementCompanies();
    fetchSalesReps();
    fetchAgencies();
    fetchContacts();
  }, []);

  // Fetch paginated buildings whenever page or rows per page change
  useEffect(() => {
    fetchBuildingsPaged(currentPage - 1, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/customers/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Fetch management companies
  const fetchManagementCompanies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/managementcompanies/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      setManagementCompanies(data || []);
    } catch (error) {
      console.error('Error fetching management companies:', error);
    }
  };

  // Fetch sales representatives
  const fetchSalesReps = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/salesperson/salespersons`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();

      const salesRepsData = data.map((rep) => ({
        ...rep,
        SalesID: rep.id.toString(),
      }));
      setSalesReps(salesRepsData || []);

    } catch (error) {
      console.error('Error fetching sales representatives:', error);
    }
  };
  // Fetch agencies
  const fetchAgencies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/inspections/agency/agencies`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      setAgencies(data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/contacts/get-all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      setContacts(data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  // Fetch paginated buildings
  const fetchBuildingsPaged = async (page, size) => {
    try {
      const url = new URL(`${API_BASE_URL}/api/setup/buildings`);
      const params = { page, size };
      Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      setBuildings(data.content || []);
      setCurrentPage(data.number + 1);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching paginated buildings:', error);
    }
  };


  // Handle field changes for building data
  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    setBuildingFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };



  const copyCutomersAddress = (customerID) => {

    const selectedCustomer = customers.find((customer) => String(customer.customerID) === String(customerID));

    if (selectedCustomer) {
      setBuildingFormData((prevData) => ({
        ...prevData,
        addressCareOf: selectedCustomer.addressCareOf,
        addressLine1: selectedCustomer.address,
        addressLine2: selectedCustomer.addressTwo,
        city: selectedCustomer.city,
        state: selectedCustomer.state,
        zip: selectedCustomer.zip,
        billingAddressCareOf: selectedCustomer.billingAddressCareOf,
        billingAddressLine1: selectedCustomer.billingAddress,
        billingAddressLine2: selectedCustomer.billingAddressTwo,
        billingCity: selectedCustomer.billingCity,
        billingState: selectedCustomer.billingState,
        billingZip: selectedCustomer.billingZip

      }));
    }
  }



  const customerToBuildingContacts = (contacts, buildingID) => {


    const buildingContacts = contacts.map((contact) => {
      return {

        buildingId: buildingID,
        contactId: contact.contactId,
        mainContact: contact.mainContact,
        agentContact: contact.agentContact,
        collectionsContact: contact.collectionsContact,
        superintendentContact: false,
        dobContact: false,


      };
    });

    return buildingContacts;

  }

  const createBuildingContacts = async (contacts, buildingID) => {
    const buildingContacts = customerToBuildingContacts(contacts, buildingID);

    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/building-contact-intersections/update/${buildingID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(buildingContacts),
      });
      const data = await response.json();

      setCustomerContacts(data || []);
    } catch (error) {
      console.error('Error creating contacts˝:', error);
    }
  };

  // Handle form submission
  const handleBuildingSubmit = async (formData, callback) => {
    try {
      // Save building data
      const response = await fetch(`${API_BASE_URL}/api/setup/buildings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include',
      });

      if (response.ok) {
        alert('Building added successfully');
        const buildingData = await response.json();

        if (customerContacts.length > 0 && buildingData.customerID) {
          createBuildingContacts(customerContacts, buildingData.buildingID, buildingData.customerID);
        }
        resetForm();
        if (callback) callback();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to add building');
      }
    } catch (error) {
      console.error('Error adding building:', error);
      alert('Failed to add building');
    }
  };




  // Reset the form to initial state
  const resetForm = () => {
    setBuildingFormData(initialBuildingFormData);
    clearCustomerContacts();
  };

  // Handle cancellation
  const handleCancel = (navigate) => {
    navigate('/setup/buildings');
  };

  // Handle pagination page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return {
    buildingFormData,
    customers,
    managementCompanies,
    salesReps,
    agencies,
    BuildingTypes,
    BuildingStatuses,
    contacts,
    buildings,
    currentPage,
    totalPages,
    rowsPerPage,
    customerContacts,
    handlePageChange,
    handleRowsPerPageChange,
    handleFieldChange,
    handleBuildingSubmit,
    resetForm,
    handleCancel,
    fetchCustomerContacts,
    clearCustomerContacts,
    copyCutomersAddress
  };
};